import { createSelector } from '@reduxjs/toolkit'
import { isAmountEligibleForLSPayLink } from '@vend/business-components'
import { trackUserEvent } from '@vend/utilities'
import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { vn } from 'vend-number'
import { isReturn } from '../../../../functions/sale'
import { trackFulfilmentNoteUsage } from '../../../../functions/tracking/trackFulfilmentNoteUsage'
import { Sale } from '../../../../models/sale'
import { SaleStatus } from '../../../../models/sale.constants'
import {
  usePaymentManager,
  useRegisterManager,
  useSaleManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectSaleReturnFor } from '../../../../state/selectors'
import {
  generateLoyaltyClaimCode,
  selectFulfilmentDetails,
  selectFulfilmentLineItemsProductCount,
  selectFulfilmentLineItemsTotalQuantity,
  selectSaleSummaryFields,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { WEB_REGISTER } from '../../../web-register.constant'
import {
  clearFulfilmentNoteEntryLocation,
  selectFulfilmentNoteEntryLocation,
} from '../../../../state/slices/uiStateSlice'
import { useGoConfirmation } from './useGoConfirmation'

const dataSelector = createSelector([selectSaleReturnFor], returnFor => ({
  returnFor,
}))

/**
 * Hook to handle layby and on-account sales processing
 *
 * @returns Object containing:
 *  - laybyOnAccountSale: Callback function to process the sales
 *  - isPayLinkModalOpen: Boolean indicating if the pay link modal is open
 *  - handlePayLinkLimitModalClose: Callback function to handle closing the payLinkLimitModal
 */
export const useLaybyOnAccountSale = (
  localId: string,
  amountTendered: number | string,
  paymentProcessing: boolean,
  setPaymentProcessing: (value: boolean) => void
) => {
  const dispatch = useDispatch()
  const goConfirmation = useGoConfirmation()
  const saleData = useAppSelector(dataSelector)
  const saleManager = useSaleManager()
  const registerManager = useRegisterManager()
  const paymentManager = usePaymentManager()
  const fulfilmentDetail = useAppSelector(selectFulfilmentDetails)[0]
  const fulfilmentNoteEntryLocation = useAppSelector(
    selectFulfilmentNoteEntryLocation
  )
  const fulfilmentProductCount = useAppSelector(
    selectFulfilmentLineItemsProductCount
  )
  const fulfilmentTotalQuantity = useAppSelector(
    selectFulfilmentLineItemsTotalQuantity
  )
  const saleTotal = useAppSelector(selectSaleSummaryFields).finals
    .inclusivePrice

  const [isPayLinkModalOpen, setIsPayLinkModalOpen] = useState(false)

  const processSale = useCallback(
    (deferredSale: Promise<Sale>) => {
      dispatch(generateLoyaltyClaimCode())

      deferredSale.then((updatedSale: Sale) => {
        trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Open Sale Confirmation', {
          id: localId,
        })
        if (updatedSale.id) {
          trackFulfilmentNoteUsage({
            saleId: updatedSale.id,
            fulfilmentDetail,
            location: fulfilmentNoteEntryLocation,
            productCount: fulfilmentProductCount,
            totalQuantity: fulfilmentTotalQuantity,
            saleTotal,
          })
        }
        dispatch(clearFulfilmentNoteEntryLocation())

        goConfirmation()
      })
    },
    [
      dispatch,
      goConfirmation,
      localId,
      fulfilmentDetail,
      fulfilmentNoteEntryLocation,
      fulfilmentProductCount,
      fulfilmentTotalQuantity,
      saleTotal,
    ]
  )

  const handlePayLinkLimitModalClose = useCallback(
    async (shouldContinuePromise: Promise<boolean>) => {
      setIsPayLinkModalOpen(false)

      try {
        if (await shouldContinuePromise) {
          processSale(saleManager.onAccountSale())
        }
      } finally {
        setPaymentProcessing(false)
      }
    },
    [processSale, saleManager, setPaymentProcessing]
  )

  const laybyOnAccountSale = useCallback(
    async (status: SaleStatus.LAYBY_SALE | SaleStatus.ACCOUNT_SALE) => {
      if (paymentProcessing) return

      let saleToProcess
      // Layby sale
      if (status === SaleStatus.LAYBY_SALE) {
        saleToProcess = saleManager.laybySale()
      } else {
        // On account sale
        const shouldCheckPaymentLinkLimit =
          getFeatureFromCache('lspay_payment_links') &&
          !(isReturn(saleData) && vn(amountTendered).lte(0)) &&
          !isAmountEligibleForLSPayLink(amountTendered)

        if (shouldCheckPaymentLinkLimit) {
          const { id: activeOutletId } = registerManager.getActiveOutlet()
          const isEligible = await paymentManager.retrievePaymentLinkAvailability(
            activeOutletId
          )

          // Show the modal and hand off the processing to the callback
          if (isEligible) {
            setIsPayLinkModalOpen(true)
            return
          }
        }

        saleToProcess = saleManager.onAccountSale()
      }

      processSale(saleToProcess)
      setPaymentProcessing(false)
    },
    [
      paymentProcessing,
      saleData,
      amountTendered,
      processSale,
      saleManager,
      registerManager,
      paymentManager,
      setPaymentProcessing,
    ]
  )

  return {
    laybyOnAccountSale,
    isPayLinkModalOpen,
    handlePayLinkLimitModalClose,
  }
}
